// context/MenuContext.js - FIXED VERSION
import React, { createContext, useReducer, useEffect, useContext, useCallback } from 'react';
import { initialState } from './initialState';
import { reducer } from './reducer';
import { sendRequest } from '../hooks/fetchData';
import {
  SET_MENUS,
  SET_USER_CHOICES,
  SET_MAIN_CATEGORY_ID,
  SET_MAIN_CATEGORY_NAME,
  SET_ACTIVE_MENU,
  SET_ACTIVE_SUB_MENU,
  SET_SUB_CATEGORY_ID,
  SET_ITEM_CHOICES,
  SET_CHOSEN_CATEGORY,
  SET_FILTERED_ITEMS,
  SET_TOTAL_MONTH_PRICE,
  SET_ORDER,
} from './actionTypes';

const MenuContext = createContext();

const getDataBasedOnMenuChoice = (data, categoryID) => {
  const items = [];
  if (!categoryID) {
    return items;
  }
  const x = data.filter((cat) => cat.categoryID === categoryID)[0]?.items;
  return x;
};

export const MenuProvider = ({ children, initialData, onOrderSuccess }) => {

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    menus: initialData.menus,
    mainCategoryID: initialData.menus[0].menuID,
    mainCategoryName: initialData.menus[0].menuName,
    activeMenu: initialData.menus[0].menuID,
    subCategoryID: initialData.menus[0].categories[0].categoryID,
    activeMenusubCategory: initialData.menus[0].categories[0].categoryID,
    totalMonthPrice:  initialData.totalMonthPrice,
    userOrder: initialData.userOrder,
    orderTimeSettings: initialData.orderTimeSettings || {},
    totalDailyPrice: initialData.totalPrice || 0,
  });

  // Initialize userChoices from initialData if available
  useEffect(() => {
   if (initialData.userOrder && initialData.userOrder.orderDetails.length > 0) {
     
  const mappedUserChoices = initialData.userOrder.orderDetails.flatMap(order =>
    
    order.mealsDetails.map(details => ({
      amount: details.count,
      itemID: details.itemId,
      itemName: details.name,
      itemPrice: details.price,
      mainCategoryName: order.resturantName
    }))
  );

      
      dispatch({ type: SET_USER_CHOICES, payload: mappedUserChoices });
    }
  }, [initialData.userOrder]);

  const chosenCategory = state.menus.filter(
    (menu) => menu.menuID === state.mainCategoryID
  )[0]?.categories || [];

  const chosenSubCategory = chosenCategory[0]?.categoryID || null;

  useEffect(() => {
    dispatch({ type: SET_SUB_CATEGORY_ID, payload: chosenSubCategory });
  }, [chosenSubCategory]);

  const filteredItems = getDataBasedOnMenuChoice(chosenCategory, state.subCategoryID);

  useEffect(() => {
    const initialChoices = filteredItems?.reduce((acc, item) => {
      acc[item.itemID] = { ...item, amount: 0 };
      return acc;
    }, {});
    dispatch({ type: SET_ITEM_CHOICES, payload: initialChoices });
  }, [filteredItems]);

  useEffect(() => {
    dispatch({ type: SET_CHOSEN_CATEGORY, payload: chosenCategory });
    dispatch({ type: SET_FILTERED_ITEMS, payload: filteredItems });
  }, [chosenCategory, filteredItems]);

  // Helper method to clear cart - MOVED FIRST to avoid dependency issues
  const clearCart = useCallback(() => {
    // Clear user choices
    dispatch({
      type: SET_USER_CHOICES,
      payload: [],
    });

    // Reset item choices amounts to 0
    const clearedItemChoices = Object.keys(state.itemChoices || {}).reduce((acc, itemID) => {
      acc[itemID] = { ...state.itemChoices[itemID], amount: 0 };
      return acc;
    }, {});

    dispatch({
      type: SET_ITEM_CHOICES,
      payload: clearedItemChoices,
    });
  }, [state.itemChoices]);

  // Method to place order
  const placeOrder = useCallback(async (userId) => {
    if (!userId || !state.userChoices?.length) {
      
      return { success: false, error: 'Invalid request' };
    }
    
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_PLACE_ORDER}?userId=${userId}`;
      
      const orderBody = state.userChoices.map(order => ({
        ItemId: order.itemID,
        Price: order.itemPrice,
        Quantity: order.amount
      }));

      const { done, data, isError, error } = await sendRequest(apiUrl,'POST', orderBody,{});

      if (done && !isError) {
        
        if (onOrderSuccess) {
          onOrderSuccess();
        }
        
        return { success: true, data: done };
      } else {
      return { success: false, error: error.message };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [state.userChoices, clearCart, onOrderSuccess]);

  // Method to replace/update an existing order (PUT)
  const replaceOrder = useCallback(async (userId, orderId) => {
    if (!userId || !orderId || !state.userChoices?.length) {
      return { success: false, error: 'Invalid request' };
    }

    try {
      const apiUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_REPLACE_ORDER}?userId=${userId}`;

      const body = {
        Id: orderId,
        Order: state.userChoices.map(order => ({
          ItemId: order.itemID,
          Price: order.itemPrice,
          Quantity: order.amount
        }))
      };

      const { done, data, isError, error } = await sendRequest(apiUrl, 'PUT', body, {});

      if (done && !isError) {
        if (onOrderSuccess) onOrderSuccess();
        return { success: true, data };
      }

      return { success: false, error: error?.message || 'Unknown error' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [state.userChoices, onOrderSuccess]);

  const cancelOrder = useCallback(async (orderId) => {
    if (!orderId) return { success: false, error: 'No orderId provided' };
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_CANCEL_ORDER}?orderId=${orderId}`;

      const { done, isError, error } = await sendRequest(apiUrl, 'DELETE', {}, {});

      if (done && !isError) {
        // clear local cart
        clearCart();
         if (onOrderSuccess) {
          onOrderSuccess();
        } 
        return { success: true };
      } 
    } catch (err) { 
      return { success: false, error: err.message };
    }
  }, [state.userChoices, clearCart]);

  

  return (
    <MenuContext.Provider
      value={{
        ...state,
        dispatch,
        // Action dispatchers
        setUserChoices: (payload) => dispatch({ type: SET_USER_CHOICES, payload }),
        setMainCategoryID: (payload) => dispatch({ type: SET_MAIN_CATEGORY_ID, payload }),
        setMainCategoryName: (payload) => dispatch({ type: SET_MAIN_CATEGORY_NAME, payload }),
        setActiveMenu: (payload) => dispatch({ type: SET_ACTIVE_MENU, payload }),
        setActiveMenusubCategory: (payload) => dispatch({ type: SET_ACTIVE_SUB_MENU, payload }),
        setSubCategoryID: (payload) => dispatch({ type: SET_SUB_CATEGORY_ID, payload }),
        setItemChoices: (payload) => dispatch({ type: SET_ITEM_CHOICES, payload }),
        
        // API methods - FIXED EXPORT NAME
        placeOrder,
        replaceOrder,
        cancelOrder,
        clearCart,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenuContext = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenuContext must be used inside a MenuProvider');
  }
  return context;
};